import { ArchiveRestoreMenuItem } from "components/Shared/ArchiveRestoreIconButton";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { assertPresence } from "utils/assertions";
import { getArchivedProductLineFilter } from "components/Products/utils";
import {
  clearUnmodifiedFields,
  modifyFieldWithSpec,
  removeRefs,
} from "utils/apollo/cacheHelpers";
import { ArchivedStatus } from "components/Shared/ArchivedStatusButtonGroup";

const fragment = gql(/* GraphQL */ `
  fragment ArchiveProductLineButtonProductLine on ProductLine {
    id
    isArchived
    skus {
      totalCount
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation UpdateProductLineArchivedStatus(
    $productLineId: UUID!
    $isArchived: Boolean!
  ) {
    setProductLineIsArchived(
      input: { productLineId: $productLineId, isArchived: $isArchived }
    ) {
      productLine {
        id
        name
        isArchived
        brandId
        skusList {
          id
          isArchived
        }
        ...ArchiveProductLineButtonProductLine
      }
    }
  }
`);

export type OnArchiveSuccess = (data: DocumentType<typeof mutation>) => void;

type Props = {
  onArchiveSuccess?: OnArchiveSuccess;
  productLine: FragmentType<typeof fragment> | null | undefined;
};

export default function ArchiveProductLineMenuItem({
  onArchiveSuccess,
  productLine: productLineFragment,
}: Props) {
  const productLine = getFragmentData(fragment, productLineFragment);

  const isArchivedAction = !productLine?.isArchived;

  const [performUpdate, { loading }] = useBrandMutation(mutation, {
    variables: productLine
      ? { isArchived: isArchivedAction, productLineId: productLine.id }
      : undefined,
    update: (cache, result) => {
      const updatedProductLine =
        result.data?.setProductLineIsArchived?.productLine;
      assertPresence(updatedProductLine);

      if (productLine?.isArchived !== updatedProductLine.isArchived) {
        const { brandId } = updatedProductLine;

        const removedFromProductLine = modifyFieldWithSpec({
          cache,
          fieldSpec: {
            fieldName: "productLinesList",
            args: {
              condition: { brandId },
              filter: getArchivedProductLineFilter(
                updatedProductLine.isArchived
                  ? ArchivedStatus.Active
                  : ArchivedStatus.Archived,
              ),
              orderBy: "CREATED_AT_ASC",
            },
          },
          modifier: removeRefs([updatedProductLine]),
        });

        clearUnmodifiedFields(cache, [removedFromProductLine]);
      }
    },
  });

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  return (
    <ArchiveRestoreMenuItem
      isArchive={isArchivedAction}
      labelSuffix="Product Line"
      onClick={() => {
        performUpdate()
          .then((result) => {
            const data = result?.data;
            assertPresence(data);
            const updatedProductLine =
              data.setProductLineIsArchived?.productLine;
            assertPresence(updatedProductLine);
            showSuccessToast(
              `${updatedProductLine.isArchived ? "Archived" : "Unarchived"} ${
                updatedProductLine.name
              }`,
            );
            onArchiveSuccess?.(data);
          })
          .catch((reason) => {
            showErrorToast(reason);
          });
      }}
      isLoading={loading}
      isDisabled={!productLine?.id || !productLine?.skus?.totalCount}
    />
  );
}
